// __private-exports
/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { useCallback } from 'react';
import { useValueEditor, type ValueEditorProps } from 'react-querybuilder';
import { Checkbox } from '../checkbox';
import { Radio } from '../radio';
import { RadioGroup } from '../radio/group';
import { Switch } from '../switch';
import { TextAreaField } from '../text-area-field';
import { TextField } from '../text-field';
import { multiValueOperators } from './constants';
import styles from './styles.module.css';
import { getValidationResult } from './utils';
import { ValueSelector } from './value-selector';
import type { InputProps } from '../input/types';
import type { QueryBuilderValueEditors } from './types';

function CheckboxValueEditor({
  className,
  disabled,
  fieldData: { name, validator },
  handleOnChange,
  rule,
  value,
}: ValueEditorProps) {
  const { valid } = getValidationResult(rule, validator);

  return (
    <Checkbox
      classNames={{ checkbox: className }}
      isDisabled={disabled}
      isSelected={value}
      onChange={handleOnChange}
      aria-invalid={!valid}
      aria-label={name}
    />
  );
}

function RadioGroupValueEditor({
  className,
  disabled,
  fieldData: { name, validator },
  handleOnChange,
  rule,
  value,
  values,
}: ValueEditorProps) {
  const { valid } = getValidationResult(rule, validator);

  return (
    <RadioGroup
      classNames={{ group: className }}
      value={value}
      isDisabled={disabled}
      onChange={handleOnChange}
      aria-invalid={!valid}
      aria-label={name}
      aria-labelledby={name}
    >
      {values?.map((option) => (
        <Radio key={option.name} value={option.value}>
          {option.label}
        </Radio>
      ))}
    </RadioGroup>
  );
}

const defaultOptions: unknown[] = [];

function SelectValueEditor({
  className,
  disabled,
  fieldData: { name, validator },
  handleOnChange,
  rule,
  type,
  value,
  values = defaultOptions,
  ...rest
}: ValueEditorProps) {
  const { valid } = getValidationResult(rule, validator);

  return (
    <ValueSelector
      {...rest}
      className={className}
      disabled={disabled}
      multiple={type === 'multiselect'}
      options={values}
      title={name}
      validation={!valid}
      value={value}
      handleOnChange={handleOnChange}
      aria-labelledby={name}
    />
  );
}

function SwitchValueEditor({
  className,
  disabled,
  fieldData: { name, validator },
  handleOnChange,
  rule,
  value,
}: ValueEditorProps) {
  const { valid } = getValidationResult(rule, validator);

  return (
    <Switch
      classNames={{ switch: className }}
      isDisabled={disabled}
      isSelected={Boolean(value)}
      onChange={handleOnChange}
      aria-invalid={!valid}
      aria-label={name}
      aria-labelledby={name}
    />
  );
}

function TextValueEditor({
  disabled,
  fieldData: { name, placeholder, validator },
  handleOnChange,
  inputType,
  rule,
  value,
  className,
}: ValueEditorProps) {
  const { valid, reasons } = getValidationResult(rule, validator);

  return (
    <TextField
      inputProps={{
        placeholder,
        type: (inputType as InputProps['type']) ?? 'text',
      }}
      size='small'
      value={value}
      classNames={{ field: className, input: { container: styles.textInput } }}
      isDisabled={disabled}
      isInvalid={!valid}
      onChange={handleOnChange}
      aria-invalid={!valid}
      aria-label={name}
      aria-labelledby={name}
      errorMessage={reasons?.join('/n')}
    />
  );
}

function TextareaValueEditor({
  className,
  disabled,
  fieldData: { name, placeholder, validator },
  handleOnChange,
  rule,
  value,
}: ValueEditorProps) {
  const { valid, reasons } = getValidationResult(rule, validator);

  return (
    <TextAreaField
      classNames={{ field: className }}
      size='small'
      errorMessage={reasons?.join('/n')}
      inputProps={{ placeholder }}
      value={value}
      isDisabled={disabled}
      isInvalid={!valid}
      onChange={handleOnChange}
      aria-label={name}
      aria-invalid={!valid}
      aria-labelledby={name}
    />
  );
}

const valueEditors: QueryBuilderValueEditors = {
  checkbox: CheckboxValueEditor,
  radio: RadioGroupValueEditor,
  select: SelectValueEditor,
  switch: SwitchValueEditor,
  text: TextValueEditor,
  textarea: TextareaValueEditor,
};

/**
 * ValueEditor - Input component for editing rule values
 *
 * Renders appropriate editor based on field type: text, checkbox, radio, select, switch, or textarea.
 * Supports multi-value operators (between, notBetween) with start/end inputs.
 *
 * @example
 * ```tsx
 * <ValueEditor
 *   fieldData={{ name: 'age', valueEditorType: 'text' }}
 *   operator="greaterThan"
 *   value="18"
 *   handleOnChange={(value) => console.log(value)}
 *   rule={{ field: 'age', operator: 'greaterThan', value: '18' }}
 * />
 * ```
 *
 * @param props - ValueEditorProps from react-querybuilder.
 * @returns The rendered value editor, or null for null/notNull operators.
 */
export function ValueEditor(props: ValueEditorProps) {
  const {
    fieldData: { name, valueEditorType },
    operator,
    rule: ruleProp,
  } = props;

  const { valueAsArray, multiValueHandler } = useValueEditor({
    ...props,
    skipHook: true,
  });

  const rule = useCallback(
    (index: number) => ({
      ...ruleProp,
      value: valueAsArray[index],
    }),
    [ruleProp, valueAsArray],
  );

  const handleRangeStartChange = useCallback(
    (value: string) => multiValueHandler(value, 0),
    [multiValueHandler],
  );

  const handleRangeEndChange = useCallback(
    (value: string) => multiValueHandler(value, 1),
    [multiValueHandler],
  );

  const type =
    (typeof valueEditorType === 'function'
      ? valueEditorType(operator)
      : valueEditorType) ?? 'text';

  const Editor = valueEditors?.[type];

  if (operator === 'null' || operator === 'notNull' || !Editor) {
    return null;
  }

  if (multiValueOperators.includes(operator)) {
    return (
      <>
        {['start', 'end'].map((term, index) => (
          <div key={`${name}-${term}`}>
            <Editor
              {...props}
              rule={rule(index)}
              value={valueAsArray[index]}
              handleOnChange={
                index ? handleRangeEndChange : handleRangeStartChange
              }
            />
          </div>
        ))}
      </>
    );
  }

  return <Editor {...props} />;
}
